import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2Icon, MinusIcon, PlusIcon, ChevronRightIcon } from 'lucide-react';
import Button from '../components/ui/Button';
import { useCart } from '../context/CartContext';
const Cart: React.FC = () => {
  const navigate = useNavigate();
  const {
    items,
    removeItem,
    updateQuantity,
    subtotal,
    clearCart
  } = useCart();
  const [promoCode, setPromoCode] = useState('');
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  // Mock fees and discounts
  const deliveryFee = 2.5;
  const tax = subtotal * 0.07;
  const discount = 0;
  const total = subtotal + deliveryFee + tax - discount;
  const handleQuantityChange = (id: string, newQuantity: number) => {
    if (newQuantity < 1) {
      removeItem(id);
    } else {
      updateQuantity(id, newQuantity);
    }
  };
  const handleApplyPromo = () => {
    setIsApplyingPromo(true);
    setTimeout(() => {
      setIsApplyingPromo(false);
      // Handle promo code logic here
    }, 1000);
  };
  if (items.length === 0) {
    return <div className="max-w-4xl mx-auto" data-id="element-262">
        <h1 className="text-2xl font-bold mb-6" data-id="element-263">Your Cart</h1>
        <div className="bg-white rounded-lg shadow-md p-6 text-center" data-id="element-264">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-265">
            <span className="text-2xl" data-id="element-266">🛒</span>
          </div>
          <h2 className="text-xl font-semibold mb-2" data-id="element-267">Your cart is empty</h2>
          <p className="text-gray-500 mb-6" data-id="element-268">
            Add some delicious items to your cart
          </p>
          <Button onClick={() => navigate('/food')} data-id="element-269">Browse Menu</Button>
        </div>
      </div>;
  }
  return <div className="max-w-4xl mx-auto pb-20 md:pb-0" data-id="element-270">
      <h1 className="text-2xl font-bold mb-6" data-id="element-271">Your Cart</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-272">
        <div className="md:col-span-2" data-id="element-273">
          <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-274">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-275">
              <h2 className="font-semibold" data-id="element-276">Items ({items.length})</h2>
              <button onClick={clearCart} className="text-sm text-red-500 hover:text-red-700 flex items-center" data-id="element-277">
                <Trash2Icon size={14} className="mr-1" data-id="element-278" />
                Clear Cart
              </button>
            </div>
            <div className="divide-y divide-gray-200" data-id="element-279">
              {items.map(item => <div key={item.id} className="p-4 flex" data-id="element-280">
                  <div className="w-20 h-20 rounded overflow-hidden flex-shrink-0" data-id="element-281">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-282" />
                  </div>
                  <div className="ml-4 flex-1" data-id="element-283">
                    <div className="flex justify-between" data-id="element-284">
                      <div data-id="element-285">
                        <h3 className="font-medium" data-id="element-286">{item.name}</h3>
                        <p className="text-sm text-gray-500" data-id="element-287">
                          {item.chineseName}
                        </p>
                      </div>
                      <div className="text-right" data-id="element-288">
                        <p className="font-medium" data-id="element-289">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-290">
                          ${item.price.toFixed(2)} each
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-3" data-id="element-291">
                      <div className="flex items-center" data-id="element-292">
                        <button onClick={() => handleQuantityChange(item.id, item.quantity - 1)} className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors" aria-label="Decrease quantity" data-id="element-293">
                          <MinusIcon size={14} data-id="element-294" />
                        </button>
                        <span className="mx-3 min-w-[20px] text-center" data-id="element-295">
                          {item.quantity}
                        </span>
                        <button onClick={() => handleQuantityChange(item.id, item.quantity + 1)} className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors" aria-label="Increase quantity" data-id="element-296">
                          <PlusIcon size={14} data-id="element-297" />
                        </button>
                      </div>
                      <button onClick={() => removeItem(item.id)} className="text-sm text-red-500 hover:text-red-700" data-id="element-298">
                        Remove
                      </button>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          <div className="mt-6" data-id="element-299">
            <Button variant="outline" fullWidth onClick={() => navigate('/food')} className="flex items-center justify-center" data-id="element-300">
              <span data-id="element-301">Continue Shopping</span>
              <ChevronRightIcon size={16} className="ml-1" data-id="element-302" />
            </Button>
          </div>
        </div>
        {/* Order summary */}
        <div className="md:col-span-1" data-id="element-303">
          <div className="bg-white rounded-lg shadow-md sticky top-6" data-id="element-304">
            <div className="p-4 border-b border-gray-200" data-id="element-305">
              <h2 className="font-semibold" data-id="element-306">Order Summary</h2>
            </div>
            <div className="p-4" data-id="element-307">
              <div className="mb-4" data-id="element-308">
                <div className="flex justify-between mb-2" data-id="element-309">
                  <input type="text" placeholder="Promo code" value={promoCode} onChange={e => setPromoCode(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-[#E84118] focus:border-[#E84118]" data-id="element-310" />
                  <button onClick={handleApplyPromo} disabled={!promoCode.trim() || isApplyingPromo} className="px-3 py-2 bg-[#E84118] text-white rounded-r-md hover:bg-[#d13a16] disabled:opacity-50" data-id="element-311">
                    {isApplyingPromo ? 'Applying...' : 'Apply'}
                  </button>
                </div>
              </div>
              <div className="space-y-2" data-id="element-312">
                <div className="flex justify-between" data-id="element-313">
                  <span className="text-gray-600" data-id="element-314">Subtotal</span>
                  <span data-id="element-315">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between" data-id="element-316">
                  <span className="text-gray-600" data-id="element-317">Delivery Fee</span>
                  <span data-id="element-318">${deliveryFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between" data-id="element-319">
                  <span className="text-gray-600" data-id="element-320">Tax</span>
                  <span data-id="element-321">${tax.toFixed(2)}</span>
                </div>
                {discount > 0 && <div className="flex justify-between text-green-600" data-id="element-322">
                    <span data-id="element-323">Discount</span>
                    <span data-id="element-324">-${discount.toFixed(2)}</span>
                  </div>}
                <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between font-bold" data-id="element-325">
                  <span data-id="element-326">Total</span>
                  <span data-id="element-327">${total.toFixed(2)}</span>
                </div>
              </div>
              <Button onClick={() => navigate('/checkout')} fullWidth size="lg" className="mt-6" data-id="element-328">
                Proceed to Checkout
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Cart;